import React, { useState, useEffect, useLayoutEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { AppProvider } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import '@shopify/polaris/build/esm/styles.css';

import useAxiosClient from './components/axiosClient';
import { identifyCrisp, resetCrispSession } from './crisp';
import { LocationProvider } from './contexts/LocationContext';
import Navigation from './components/ui/Navigation';
import Page from './components/ui/Page';
import Frame from './components/ui/Frame';
import Loading from './components/ui/Loading';
import AuthContainer from './components/auth/AuthContainer';
import Dashboard from "./components/Dashboard/Dashboard";
import StaffManagement from "./components/StaffManagement/StaffManagement";
import Payroll from "./components/Payroll/Payroll";
import CreatePayroll from "./components/Payroll/CreatePayroll";
import AddStaffPage from "./components/StaffManagement/AddStaffPage";
import AddNonShopifyStaffPage from "./components/StaffManagement/AddNonShopifyStaffPage";
import EditStaffPage from "./components/StaffManagement/EditStaffPage";
import StaffDetails from "./components/StaffManagement/StaffDetails";
import Tasklist from "./components/Tasklist/Tasklist";
import AddTasklist from "./components/Tasklist/AddTasklist";
import TasklistDetails from "./components/Tasklist/TasklistDetails";
import EditTasklist from "./components/Tasklist/EditTasklist";
import Reports from "./components/Reports/Reports";
import Schedule from "./components/Schedule/Schedule";
import Settings from "./components/Settings/Settings";
import Pricing from "./components/Pricing/Pricing";

import TimeOff from "./components/TimeOff/TimeOff";
import CreatePolicy from "./components/TimeOff/CreatePolicy";
import EditPolicy from "./components/TimeOff/EditPolicy";
import Policy from "./components/TimeOff/Policy";
import CreateTimeOff from "./components/TimeOff/CreateTimeOff";
import EditTimeOff from "./components/TimeOff/EditTimeOff";
import BulkImportStaff from "./components/StaffManagement/BulkImportStaff";
import Faq from "./components/Faq/Faq";
import AppsumoActivate from "./components/appsumo/AppsumoActivate";
import WixEmbedded from "./components/wix/WixEmbedded";
import safeStorage from "./utils/safeStorage";
import { isWix } from "./utils/platform";
import AppFooter from './components/AppFooter';
import Onboarding from './components/Onboarding/Onboarding';
import Welcome from './components/Onboarding/Welcome';
function App() {

  const navigate = useNavigate();
  const location = useLocation();
  const axiosClient = useAxiosClient();

  const [planId, setPlanId] = useState(null);
  const [isFreemium, setIsFreemium] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // Server-owned: true only for accounts created after the guide shipped that
  // have not finished or skipped it. The migration backfilled every older
  // account as finished, so this can never be true for them.
  const [onboardingPending, setOnboardingPending] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  // The hold below is chromeless, so it must not be able to last. The status
  // call carries the app's usual 10s axios timeout, and ten seconds of blank
  // page is a worse failure than the flash it exists to prevent.
  const [statusHoldExpired, setStatusHoldExpired] = useState(false);

  // The welcome screen shows once, ahead of the setup guide. Remembered rather
  // than held in state alone, so a reload during setup does not replay the
  // pitch to someone who is already three steps into it. safeStorage because a
  // merchant in a locked-down or private window still has to get past it - it
  // degrades to memory, which just means they see it once more.
  //
  // Keyed PER ACCOUNT. One browser signs in to more than one - a merchant with
  // two businesses, a partner setting a client up, anyone testing - and while
  // this was a single browser-wide key, the first account to dismiss the screen
  // swallowed the first run of every account created on that browser
  // afterwards. Starts false and is read after login, because which account
  // this is only becomes known then; the gate below also requires
  // onboardingChecked, which lands after a round trip, so the false start never
  // shows the screen to someone who has already dismissed it.
  const [welcomeSeen, setWelcomeSeen] = useState(false);

  const welcomeSeenKey = () => {
    try {
      const raw = safeStorage.get('user') || localStorage.getItem('user');
      const id = raw ? JSON.parse(raw)?.id : null;

      return id ? 'zon_welcome_seen:' + id : 'zon_welcome_seen';
    } catch {
      // Unparseable or unreadable storage: fall back to the shared key rather
      // than replaying the screen on every single load.
      return 'zon_welcome_seen';
    }
  };

  const dismissWelcome = () => {
    safeStorage.set(welcomeSeenKey(), '1');
    // The browser-wide key is never read again. Drop it so a stale '1' left by
    // the previous build cannot keep suppressing the screen.
    safeStorage.remove('zon_welcome_seen');
    setWelcomeSeen(true);
  };
  const [shopName, setShopName] = useState('');
  // /dashboard-stats already returns seat usage; the sidebar card reads it from
  // here rather than making a second request for numbers we were discarding.
  const [shopStats, setShopStats] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, false = not authenticated, true = authenticated
  
  // Function to fetch shop data
  const fetchShopData = async () => {
    try {
      const response = await axiosClient.get('/dashboard-stats');
      if (response.data.success) {
        // The name guard must not gate the stats: a shop with no name set still
        // has seats, and the card would silently stay empty for them.
        if (response.data.name) setShopName(response.data.name);
        setShopStats(response.data.stats || null);
      }
    } catch (error) {
      // Error handled silently
    }
  };

  useEffect(() => {
    const initializeApp = async () => {
      setIsLoading(true);
      try {
        // --- START FIX: Check URL for token from Clover/Backend redirect ---
        const searchParams = new URLSearchParams(window.location.search);
        const urlToken = searchParams.get('token');

        if (urlToken) {
          // 1. Save the token to storage so the rest of the app can use it
          safeStorage.set('authToken', urlToken);

          // 2. Clean the URL so the token doesn't sit in the browser address bar (Security + UX)
          window.history.replaceState({}, document.title, window.location.pathname);
        }
        // --- END FIX ---

        // Check authentication status (Now checking storage AFTER we potentially saved the URL token)
        const authToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
        const isLoggedIn = localStorage.getItem('isLoggedIn');
        const userInfo = localStorage.getItem('user');

        if (authToken) {
          setIsAuthenticated(true);
          // Skip plan check - set default values for free app
          setPlanId(1);
          setIsFreemium(1);
          // Fetch shop data
          fetchShopData();
        } else if (isLoggedIn === 'true' && userInfo) {
          setIsAuthenticated(true);
          setPlanId(1);
          setIsFreemium(1);
          // Fetch shop data
          fetchShopData();
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
        setPlanId(null);
        setIsFreemium(0);
      } finally {
        setIsLoading(false);
      }
    };

    // Listen for auth expiration events
    const handleAuthExpired = () => {
      setIsAuthenticated(false);
      setPlanId(null);
      setIsFreemium(0);
    };

    window.addEventListener('auth-expired', handleAuthExpired);

    initializeApp();

    // Cleanup event listener
    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, []);
  useEffect(() => {
    if (isAuthenticated !== true || onboardingChecked) return undefined;

    const timer = setTimeout(() => setStatusHoldExpired(true), 2500);

    return () => clearTimeout(timer);
  }, [isAuthenticated, onboardingChecked]);

  /**
   * The boot screen in index.html comes down here, and not a moment earlier.
   *
   * It used to be torn down as soon as React mounted, which is not the same as
   * having something to show: the auth check and the onboarding-status call are
   * both still in flight then. The merchant got the branded screen, then a bare
   * spinner for the length of those two round trips, then the page - two
   * loading states where one will do.
   *
   * "Ready" is the first moment renderMainContent would draw real content: the
   * login form for a signed-out visitor, or - once the status call has landed
   * or its 2.5s ceiling has passed - the welcome screen, the guide or the
   * dashboard. Every one of those resolves, and the boot script has its own 10s
   * "taking longer than usual" fallback underneath, so this cannot strand
   * anyone on a progress bar.
   *
   * useLayoutEffect so the fade starts in the same frame the content first
   * paints, rather than one frame later with both on screen. done() is
   * idempotent, which is what makes it safe under StrictMode's double invoke
   * and on every later render.
   */
  const bootReady =
    !isLoading
    && isAuthenticated !== null
    && (isAuthenticated === false || onboardingChecked || statusHoldExpired);

  useLayoutEffect(() => {
    if (bootReady) window.__zsBoot?.done();
  }, [bootReady]);

  // The welcome flag is per account, and which account this is only becomes
  // known at login - so it is read here rather than in useState at mount.
  useEffect(() => {
    if (isAuthenticated !== true) return;

    setWelcomeSeen(safeStorage.get(welcomeSeenKey()) === '1');
  }, [isAuthenticated]);

  // Ask once per session whether the guide is still pending. Not gated on
  // isAuthenticated alone: the endpoint needs the token, so it runs after login.
  useEffect(() => {
    if (isAuthenticated !== true || onboardingChecked) return;

    let cancelled = false;

    (async () => {
      try {
        const response = await axiosClient.get('/onboarding-status');
        if (!cancelled) setOnboardingPending(Boolean(response.data?.pending));

        // A 200 carrying pending:false is byte for byte what a finished account
        // looks like, whatever the reason - a missing table, or a row this
        // account never earned. Say which, or a first run that silently never
        // happens is indistinguishable from normal operation.
        if (response.data?.ready === false) {
          console.warn(
            '[onboarding] the API reports onboarding_states is unavailable. '
            + 'Run `php artisan migrate` on the API host. Until then no new '
            + 'account will see the welcome screen or the setup guide.',
          );
        } else if (!response.data?.pending) {
          console.info(
            '[onboarding] this account is already marked finished '
            + '(completed_at/dismissed_at set), so the welcome screen and the '
            + 'setup guide are both skipped.',
          );
        }
      } catch (error) {
        // A welcome screen is not worth blocking the app over - fall through to
        // the dashboard. Logged rather than swallowed: this is the one path
        // that turns a broken endpoint into a silently missing first run.
        console.warn('[onboarding] status check failed; showing the dashboard.', error);
        if (!cancelled) setOnboardingPending(false);
      } finally {
        if (!cancelled) setOnboardingChecked(true);
      }
    })();

    return () => { cancelled = true; };
    // axiosClient is deliberately NOT a dependency. useAxiosClient() calls
    // axios.create() on every render, so it is a new object every time - with
    // it listed here this effect tore down and re-ran on every single render,
    // and each teardown set cancelled = true on a request that was still in
    // flight, throwing its answer away. During signup the app renders several
    // times in a row (auth state, shop data, locations, Crisp), so the status
    // call could be cancelled repeatedly and the first run never resolve.
  }, [isAuthenticated, onboardingChecked]);

  const renderMainContent = () => {
    // Show loading during initial authentication check or when isLoading is true
    if (isLoading || isAuthenticated === null) {
      return (
        <Frame shopName={shopName}>
          <Loading />
        </Frame>
      );
    }

    if (isAuthenticated === true) {
      // Hold the home route until we know, or a new account sees the dashboard
      // flash before the guide replaces it.
      if (!onboardingChecked) {
        return (
          <Frame shopName={shopName}>
            <Loading />
          </Frame>
        );
      }

      // Swap rather than redirect, as the Shopify app does: the guide IS the
      // home page while it is pending, so a reload cannot escape it and the
      // address bar stays put.
      if (onboardingPending) {
        // The welcome screen is handled before the shell is rendered at all -
        // see showFirstRunWelcome below - so by the time this runs it has been
        // dismissed and the guide is what belongs here.
        return <Onboarding onFinish={() => setOnboardingPending(false)} />;
      }

      return <Dashboard />;
    }

    // Show login form for unauthenticated users (isAuthenticated === false)
    return <AuthContainer onLogin={handleLogin} />;
  };

  const handleLogin = (token, userData) => {
    if (token) {
      localStorage.setItem('authToken', token);
    }

    // Handle signup case where no token is provided
    if (userData.user && !token) {
      // For signup, we'll set a temporary authenticated state
      localStorage.setItem('user', JSON.stringify(userData.user));
      localStorage.setItem('isLoggedIn', 'true');
    }

    setIsAuthenticated(true);
    // Set default values for free app - skip plan checking
    setPlanId(1); // Set a default plan ID
    setIsFreemium(1); // Set as freemium to allow access
    
    // Fetch shop data after login
    fetchShopData();

    // Logging in is a client-side transition - index.html is not reloaded - so
    // the identify at module load ran while there was still no token. Run it
    // now that there is one, or support sees this merchant as anonymous for
    // the whole session.
    identifyCrisp();

    // Signal providers that need the token (e.g. LocationContext) to (re)load —
    // they mounted before auth, so without this locations only appear after a refresh.
    window.dispatchEvent(new Event('auth-changed'));

    // If there's a redirect URL, navigate to it
    if (userData.redirectUrl) {
      // For now, just navigate to dashboard since external redirect might cause issues
      navigate('/');
    }
  };

  const handleLogout = () => {
    // BEFORE the storage clear. Crisp keeps its own session id in
    // localStorage, so without this the next merchant to sign in on this
    // browser resumes the previous merchant's conversation and overwrites
    // their contact record.
    resetCrispSession();

    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('currentLocationId');
    // Stop Google One Tap from silently auto-selecting the last account next time.
    try { window.google?.accounts?.id?.disableAutoSelect(); } catch (e) { /* noop */ }
    setIsAuthenticated(false);
    setPlanId(null);
    setIsFreemium(0);
    setShopName(''); // Clear shop name on logout
    navigate('/');
  };


  // AppSumo OAuth landing (the redirect URL registered in the Partner Portal).
  // Rendered BEFORE the auth gate on purpose: a Sumo-ling arriving from AppSumo
  // has no ZonStaff account yet, so the normal login screen would trap them and
  // burn their single-use ?code=.
  const isAppsumoLanding =
    typeof window !== 'undefined' &&
    window.location.pathname.replace(/\/+$/, '').toLowerCase() === '/appsumo';

  // Wix embedded dashboard landing. Like /appsumo this must render BEFORE the
  // auth gate: a Wix site owner has no ZonStaff password (their account email is
  // synthetic), so the login screen would be a dead end inside the Wix iframe.
  const isWixLanding =
    typeof window !== 'undefined' &&
    window.location.pathname.replace(/\/+$/, '').toLowerCase() === '/wix';

  /**
   * The first-run welcome screen renders OUTSIDE the app shell, the same way
   * the /wix and /appsumo landings below do.
   *
   * Inside the shell it competed with itself. The screen exists to produce one
   * click, and the sidebar offered eleven other exits - to pages that are empty
   * until setup is done, so following any of them is a worse first impression
   * than the screen the merchant just left. It also put two language switchers
   * on one screen (the app header's and this page's own) and three copies of
   * the wordmark (sidebar, hero, footer), and showed "Upgrade Plan" to someone
   * who has not added a single person yet.
   *
   * onboardingChecked is part of the condition so this cannot flash before the
   * status call has landed and then swap.
   */
  const showFirstRunWelcome =
    isAuthenticated === true && onboardingChecked && onboardingPending && !welcomeSeen;

  /**
   * Opening /Welcome deliberately renders bare as well.
   *
   * It is the same screen, so putting chrome around it here and not on a real
   * install would make the only way anyone can preview it lie about what a new
   * merchant actually sees.
   *
   * The router's location is read rather than window.location.pathname because
   * "Get started" navigates client side, and window.location would be read from
   * a stale render.
   *
   * Checked BEFORE showFirstRunWelcome so the two can never both match: on this
   * route the button always leads to the guide and never marks the screen as
   * seen. Previewing it must not suppress it for a genuinely new account on
   * this browser, and marking it seen here would leave the route re-rendering
   * itself with nowhere to go.
   */
  const isWelcomeRoute =
    location.pathname.replace(/\/+$/, '').toLowerCase() === '/welcome';

  /**
   * Hold chromeless until the status call lands.
   *
   * The two answers render completely different pages - the bare welcome
   * screen, or the full shell - and the holding state used to sit INSIDE
   * <Frame>, so the sidebar, header and seat meter painted first and were then
   * thrown away whole when the welcome screen replaced them. That is what read
   * as the dashboard flashing up and disappearing on every refresh.
   *
   * Same background as the welcome screen, so the handover is a spinner
   * becoming content rather than one page becoming another.
   *
   * renderMainContent keeps its own !onboardingChecked branch: once the ceiling
   * above expires this falls through to the shell, and that branch is what
   * holds the home route while a slow answer is still on its way.
   */
  if (isAuthenticated === true && !onboardingChecked && !statusHoldExpired) {
    return (
      <AppProvider i18n={enTranslations}>
        <div
          style={{
            minHeight: '100vh',
            background: '#f1f1f1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loading />
        </div>
      </AppProvider>
    );
  }

  if (isAuthenticated === true && isWelcomeRoute) {
    return (
      <AppProvider i18n={enTranslations}>
        <div style={{ minHeight: '100vh', background: '#f1f1f1', padding: '20px 28px 40px' }}>
          <Welcome onStart={() => navigate('/Onboarding')} />
        </div>
      </AppProvider>
    );
  }

  if (showFirstRunWelcome) {
    return (
      <AppProvider i18n={enTranslations}>
        {/* The shell normally pays this gutter. */}
        <div style={{ minHeight: '100vh', background: '#f1f1f1', padding: '20px 28px 40px' }}>
          <Welcome onStart={dismissWelcome} />
        </div>
      </AppProvider>
    );
  }

  if (isWixLanding) {
    return (
      <AppProvider i18n={enTranslations}>
        <WixEmbedded onLogin={handleLogin} />
      </AppProvider>
    );
  }

  if (isAppsumoLanding) {
    return (
      <AppProvider i18n={enTranslations}>
        <AppsumoActivate onLogin={handleLogin} />
      </AppProvider>
    );
  }

  return (
    <AppProvider i18n={enTranslations}>
      <LocationProvider>
        {isAuthenticated !== true ? (
          (isAuthenticated === null || isLoading) ? (
            <Loading />
          ) : (
            <AuthContainer onLogin={handleLogin} />
          )
        ) : (
        <Frame
          navigation={<Navigation onLogout={handleLogout} stats={shopStats} />}
          shopName={shopName}
        >
          <Page fullWidth>
            <Routes>
              <Route
                path="/"
                element={
                  <React.Suspense
                    fallback={
                      <Frame shopName={shopName}>
                        <Loading />
                      </Frame>
                    }
                  >
                    {renderMainContent()}
                  </React.Suspense>
                }
              />



              {/* /Welcome is handled above, OUTSIDE the shell - see
                  isWelcomeRoute. No Route here on purpose: one inside <Frame>
                  could never be reached, and would read as though the screen
                  still had chrome. */}
              <Route
                path="Onboarding"
                element={
                  <Onboarding />
                }
              />
              <Route
                path="StaffManagement"
                element={
                  <StaffManagement />
                }
              />
              <Route
                path="/StaffManagement/bulk-import"
                element={
                  <BulkImportStaff />
                }
              />
              {/* Wix App Market apps must bill through the Wix Billing System —
                  using our own Stripe checkout to unlock functionality is an
                  explicit rejection trigger. The route is blocked (not just
                  hidden in the nav) so a typed URL can't reach it either. */}
              <Route
                path="Pricing"
                element={
                  isWix() ? <Navigate to="/" replace /> : <Pricing />
                }
              />

              <Route
                path="Reports"
                element={
                  <Reports />
                }
              />

              <Route
                path="TimeOff"
                element={
                  <TimeOff />
                }
              />


              <Route
                path="/TimeOff/CreateTimeOff"
                element={
                  <CreateTimeOff />
                }
              />
              <Route
                path="/TimeOff/createTimeOff"
                element={
                  <CreateTimeOff />
                }
              />

              <Route
                path="/TimeOff/EditTimeOff/:id"
                element={
                  <EditTimeOff />
                }
              />


              <Route
                path="/TimeOff/Policy"
                element={
                  <Policy />
                }
              />

              <Route
                path="/TimeOff/Policy/CreatePolicy"
                element={
                  <CreatePolicy />
                }
              />
              <Route
                path="/TimeOff/Policy/createPolicy"
                element={
                  <CreatePolicy />
                }
              />

              <Route
                path="/TimeOff/Policy/PolicyDetails/:id"
                element={
                  <EditPolicy />
                }
              />
              <Route
                path="/TimeOff/Policy/policyDetails/:id"
                element={
                  <EditPolicy />
                }
              />

              <Route
                path="Schedule"
                element={
                  <Schedule />
                }
              />

              <Route
                path="/Tasklist/EditTasklist/:id"
                element={
                  <EditTasklist />
                }
              />

              <Route
                path="/Tasklist/TasklistDetails/:id"
                element={
                  <TasklistDetails />
                }
              />


              <Route
                path="Tasklist"
                element={
                  <Tasklist />
                }
              />

              <Route
                path="Settings"
                element={
                  <Settings />
                }
              />

              <Route
                path="Faq"
                element={
                  <Faq />
                }
              />

              <Route
                path="/Tasklist/AddTasklist"
                element={
                  <AddTasklist />
                }
              />





              <Route
                path="Payroll"
                element={
                  <Payroll />
                }
              />

              <Route
                path="/Payroll/payrollCreate/:id"
                element={
                  <CreatePayroll />
                }
              />

              <Route
                path="/StaffManagement/AddStaffPage"
                element={
                  <AddStaffPage />
                }
              />

              <Route
                path="/StaffManagement/AddNonShopifyStaffPage"
                element={
                  <AddNonShopifyStaffPage />
                }
              />

              <Route
                path="/StaffManagement/editStaff/:id"
                element={
                  <EditStaffPage />
                }
              />


              <Route
                path="/StaffManagement/staffDetails/:id"
                element={
                  <StaffDetails />
                }
              />


              {/* Catch-all route for unmatched URLs */}
              <Route
                path="*"
                element={
                  <React.Suspense
                    fallback={
                      <Frame shopName={shopName}>
                        <Loading />
                      </Frame>
                    }
                  >
                    {renderMainContent()}
                  </React.Suspense>
                }
              />

            </Routes>

            {/* One footer for every route: inside <Page> so it inherits the page
                width, after <Routes> so it sits under whatever the route drew.
                Per-page copies would mean thirty-odd call sites and one of them
                would always be missed. */}
            <AppFooter />
          </Page>
        </Frame>
        )}
      </LocationProvider>
    </AppProvider>
  );
}

export default App;
